import { uiText } from '../../utils/i18n';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import type { ActivityKind, BotInfo, ChatMessage, ChatSession, FileAttachment, ToolCall } from '../../types';
import { useStore } from '../../store';
import { Avatar } from '../shared/Avatar';
import { BotAvatar } from '../shared/BotAvatar';
import { getAvatar } from '../../utils/avatar-store';
import { useT } from '../../utils/i18n';
import { fileExt, isTextPreviewable, isEmbedPreviewable, isOfficePreviewable, generateId } from './helpers';
import styles from './MessageList.module.css';
import { AuthenticatedImage } from '../shared/AuthenticatedImage';
import { downloadAuthenticatedFile, fetchAuthenticatedFile } from '../../hooks/useAuthenticatedBlobUrl';
import { getNavigationMessages, getVisibleUserText, preprocessMarkdown } from './message-contract';

const assistantMarkdownSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    span: [
      ...(defaultSchema.attributes?.span || []),
      ['className', 'nx-danger', 'nx-success', 'nx-highlight', 'nx-mention'],
    ],
  },
};

interface MessageListProps {
  session: ChatSession;
  bots: BotInfo[];
  onFilePreview?: (file: FileAttachment) => void;
  /** DM partner info for rendering correct name/avatar in DM chats */
  dmPartner?: { name?: string; avatarUrl?: string } | null;
  /** Quick reply callback — only for 1:1 agent chats */
  onQuickReply?: (text: string) => void;
}

function MentionedText({ text }: { text: string }) {
  const parts = text.split(/(\n|(?<![a-zA-Z0-9.])@[\w\p{L}\p{N}-]+)/gu);
  return <>{parts.map((part, index) => {
    if (part === '\n') return <br key={index} />;
    if (/^@[\w\p{L}\p{N}-]+$/u.test(part)) return <span key={index} className="nx-mention">{part}</span>;
    return part;
  })}</>;
}

function formatToolLabel(name: string): string {
  switch (name) {
    case 'Reasoning': return uiText("分析思路");
    case 'Read': return uiText("读取内容");
    case 'Write':
    case 'Edit':
    case 'FileChange': return uiText("修改文件");
    case 'Shell':
    case 'Bash': return uiText("执行命令");
    case 'Test': return uiText("验证结果");
    case 'Glob': return uiText("查找文件");
    case 'Grep':
    case 'Search': return uiText("搜索内容");
    case 'WebSearch': return uiText("联网搜索");
    case 'WebFetch': return uiText("读取网页");
    case 'McpTool': return uiText("使用工具");
    case 'Plan': return uiText("更新计划");
    case 'Agent': return uiText("协作 Agent");
    case 'Task': return uiText("执行任务");
    case 'AskUserQuestion': return uiText("等待确认");
    case 'Error': return uiText("执行异常");
    default: return name;
  }
}

function activityKind(tool: ToolCall): ActivityKind {
  if (tool.kind) return tool.kind;
  switch (tool.name) {
    case 'Reasoning': return 'reasoning';
    case 'Read': return 'read';
    case 'Glob':
    case 'Grep':
    case 'Search': return 'search';
    case 'Write':
    case 'Edit':
    case 'FileChange': return 'write';
    case 'WebSearch':
    case 'WebFetch': return 'web';
    case 'Plan': return 'plan';
    case 'Agent':
    case 'Task': return 'agent';
    case 'AskUserQuestion': return 'question';
    case 'Error': return 'error';
    default: return 'command';
  }
}

function activityIcon(kind: ActivityKind): string {
  switch (kind) {
    case 'reasoning': return '✦';
    case 'read': return '↳';
    case 'search': return '⌕';
    case 'write': return '+';
    case 'command': return '>_';
    case 'test': return '✓';
    case 'web': return '◎';
    case 'mcp': return '◇';
    case 'plan': return '≡';
    case 'agent': return '◌';
    case 'question': return '?';
    case 'error': return '!';
    default: return '·';
  }
}

function activityKindClass(kind: ActivityKind): string {
  switch (kind) {
    case 'reasoning': return styles.activityKindReasoning;
    case 'read': return styles.activityKindRead;
    case 'search': return styles.activityKindSearch;
    case 'write': return styles.activityKindWrite;
    case 'command': return styles.activityKindCommand;
    case 'test': return styles.activityKindTest;
    case 'web': return styles.activityKindWeb;
    case 'mcp': return styles.activityKindMcp;
    case 'plan': return styles.activityKindPlan;
    case 'agent': return styles.activityKindAgent;
    case 'question': return styles.activityKindQuestion;
    case 'error': return styles.activityKindError;
    default: return styles.activityKindOther;
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function MessageList({ session, bots, onFilePreview, dmPartner, onQuickReply }: MessageListProps) {
  const t = useT();
  const endRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevSessionId = useRef<string | null>(null);
  const prevMsgCount = useRef(session.messages.length);
  const userScrolledUp = useRef(false);

  // Track whether user has scrolled away from bottom
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onScroll = () => {
      const threshold = 80;
      const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
      userScrolledUp.current = !atBottom;
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const isSessionSwitch = prevSessionId.current !== session.id;

    if (isSessionSwitch) {
      // Session switch or first render: jump to bottom after DOM paints
      prevSessionId.current = session.id;
      prevMsgCount.current = session.messages.length;
      userScrolledUp.current = false;
      requestAnimationFrame(() => {
        const container = containerRef.current;
        if (container) container.scrollTop = container.scrollHeight;
      });
      return;
    }

    if (session.messages.length !== prevMsgCount.current) {
      const delta = session.messages.length - prevMsgCount.current;
      if (delta > 2) {
        // Bulk history load (e.g. DM history): jump instantly
        requestAnimationFrame(() => {
          const container = containerRef.current;
          if (container) container.scrollTop = container.scrollHeight;
        });
      } else {
        // Single new message: smooth scroll
        endRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
      userScrolledUp.current = false;
    } else if (!userScrolledUp.current) {
      // Streaming text update: only auto-scroll if user is near bottom
      endRef.current?.scrollIntoView({ behavior: 'instant' });
    }
    prevMsgCount.current = session.messages.length;
  }, [session.id, session.messages.length, session.messages[session.messages.length - 1]?.text]);

  const handleFileClick = onFilePreview || (() => {});

  const addMessage = useStore((s) => s.addMessage);
  const getOrCreateBotSession = useStore((s) => s.getOrCreateBotSession);
  const [fwdText, setFwdText] = useState<string | null>(null);

  const handleForward = useCallback((text: string) => {
    setFwdText(text);
  }, []);

  const handleFwdPick = useCallback((targetBot: string) => {
    if (!fwdText) return;
    const sessionId = getOrCreateBotSession(targetBot);
    addMessage(sessionId, {
      id: `fwd-${generateId()}`,
      type: 'user',
      text: fwdText,
      timestamp: Date.now(),
    });
    setFwdText(null);
  }, [fwdText, getOrCreateBotSession, addMessage]);

  const handleFwdClose = useCallback(() => setFwdText(null), []);

  const [navOpen, setNavOpen] = useState(false);
  const navigationMessages = useMemo(
    () => getNavigationMessages(session.messages),
    [session.messages],
  );

  const scrollToMessage = useCallback((msgId: string) => {
    const container = containerRef.current;
    if (!container) return;
    const el = container.querySelector(`[data-msg-id="${msgId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Brief highlight
      el.classList.add(styles.msgHighlight);
      setTimeout(() => el.classList.remove(styles.msgHighlight), 1500);
    }
  }, []);

  if (session.messages.length === 0) {
    return (
      <div className={styles.msgListWrap}>
        <div className={styles.container} ref={containerRef}>
          <div className={styles.empty}>
            <p>{t('chat.startConversation')} <strong>{dmPartner?.name || (session.botName.startsWith('dm:') ? (session.title !== 'New Chat' ? session.title : '') : session.botName)}</strong></p>
          </div>
          <div ref={endRef} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.msgListWrap}>
      {/* Nav toggle button */}
      {navigationMessages.length > 1 && (
        <button className={styles.navToggle} onClick={() => setNavOpen((v) => !v)} aria-label={uiText("消息导航")}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="15" y2="12" /><line x1="3" y1="18" x2="18" y2="18" />
          </svg>
        </button>
      )}

      {/* Nav panel */}
      {navOpen && navigationMessages.length > 1 && (
        <MsgNavPanel
          messages={navigationMessages}
          onJump={(id) => { scrollToMessage(id); }}
          onClose={() => setNavOpen(false)}
        />
      )}

      <div className={styles.container} ref={containerRef}>
        {session.messages.map((msg, idx) => (
          <div key={msg.id} data-msg-id={msg.id}>
            <MessageBubble msg={msg} botName={session.botName} sessionTitle={session.title} dmPartner={dmPartner} onFileClick={handleFileClick} onForward={handleForward} t={t} isLast={idx === session.messages.length - 1} onQuickReply={onQuickReply} />
          </div>
        ))}
        <div ref={endRef} />
        {fwdText != null && <ForwardPicker onPick={handleFwdPick} onClose={handleFwdClose} />}
      </div>
    </div>
  );
}

function getAttachIcon(file: FileAttachment): string {
  const ext = fileExt(file.name);
  const icons: Record<string, string> = {
    md: '📝', markdown: '📝', pdf: '📕', json: '📋', xml: '📋', yaml: '📋', yml: '📋',
    js: '📜', ts: '📜', jsx: '📜', tsx: '📜', py: '📜', csv: '📊', xlsx: '📊',
    docx: '📄', doc: '📄', txt: '📄',
  };
  if (file.type.startsWith('image/')) return '🖼';
  return icons[ext] || '📎';
}

function canPreviewInline(file: FileAttachment): boolean {
  return isTextPreviewable(file.name, file.type) || isEmbedPreviewable(file.name) || !!isOfficePreviewable(file.name);
}

/** Check if a file is text-based and can show inline content preview */
function canShowContentPreview(file: FileAttachment): boolean {
  const ext = fileExt(file.name);
  return ['md', 'markdown', 'txt', 'html', 'json', 'xml', 'yaml', 'yml', 'js', 'ts', 'jsx', 'tsx', 'py', 'css', 'csv'].includes(ext);
}

/** Get accent color for file type */
function getExtAccent(ext: string): string {
  const map: Record<string, string> = {
    html: '#c54a2f', pdf: '#c54a2f',
    md: '#5a6a2a', markdown: '#5a6a2a', txt: '#5a6a2a',
    json: '#7850c8', xml: '#7850c8', yaml: '#7850c8', yml: '#7850c8',
    js: '#c79a3a', ts: '#5a8ab8', jsx: '#c79a3a', tsx: '#5a8ab8',
    py: '#5a8ab8', css: '#5a8ab8',
    csv: '#2a8a3c', xlsx: '#2a8a3c', xls: '#2a8a3c',
    docx: '#3a6acc', doc: '#3a6acc',
    pptx: '#d2641e', ppt: '#d2641e',
    zip: '#a07828',
  };
  return map[ext] || '#5a6a2a';
}

/** File card with content preview (Feishu-style) */
const FilePreviewCard = memo(function FilePreviewCard({ att, onFileClick }: { att: FileAttachment; onFileClick: (f: FileAttachment) => void }) {
  const ext = fileExt(att.name);
  const titleWithoutExt = att.name.replace(/\.[^.]+$/, '');
  const accent = getExtAccent(ext);
  const isPreviewable = canPreviewInline(att);
  const showContentPreview = canShowContentPreview(att);
  const [previewLines, setPreviewLines] = useState<string[] | null>(null);

  useEffect(() => {
    if (!showContentPreview || !att.url) return;
    let cancelled = false;
    fetchAuthenticatedFile(att.url)
      .then((r) => r.ok ? r.text() : null)
      .then((raw) => {
        if (cancelled || !raw) return;
        let text = raw;
        // For HTML files, strip tags and extract readable text
        if (ext === 'html' || ext === 'htm') {
          // Extract <title> if present
          const titleMatch = raw.match(/<title[^>]*>(.*?)<\/title>/i);
          // Extract body text content, strip tags
          const bodyMatch = raw.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
          const bodyHtml = bodyMatch ? bodyMatch[1] : raw;
          const stripped = bodyHtml
            .replace(/<script[\s\S]*?<\/script>/gi, '')
            .replace(/<style[\s\S]*?<\/style>/gi, '')
            .replace(/<[^>]+>/g, ' ')
            .replace(/&nbsp;/g, ' ')
            .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
            .replace(/\s+/g, ' ')
            .trim();
          const lines: string[] = [];
          if (titleMatch?.[1]) lines.push(`# ${titleMatch[1].trim()}`);
          // Split into ~80 char chunks as "paragraphs"
          const words = stripped.split(' ');
          let line = '';
          for (const w of words) {
            if ((line + ' ' + w).length > 80 && line) { lines.push(line); line = w; }
            else { line = line ? line + ' ' + w : w; }
            if (lines.length >= 10) break;
          }
          if (line && lines.length < 10) lines.push(line);
          text = lines.join('\n');
        }
        const snippet = text.slice(0, 600);
        setPreviewLines(snippet.split('\n').slice(0, 12));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [att.url, showContentPreview, ext]);

  const actionEl = (
    <button type="button" className={styles.fileCardAction} onClick={(e) => { e.stopPropagation(); void downloadAuthenticatedFile(att); }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
        <path d="M12 4v12M6 10l6 6 6-6M4 20h16" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      {uiText("下载")}
    </button>
  );

  const card = (
    <div className={styles.fileCardPreview} onClick={() => canPreviewInline(att) && onFileClick(att)}>
      {/* header row */}
      <div className={styles.fileCardPreviewHeader}>
        <div className={styles.fileCardIconSm}>
          <div className={styles.fileCardCornerFold} />
          <div className={styles.fileCardCornerLine} />
          <span className={styles.fileCardExtSm} style={{ background: accent }}>{ext}</span>
        </div>
        <div className={styles.fileCardPreviewTitle}>
          <span className={styles.fileCardPreviewName}>{titleWithoutExt}</span>
          {att.size > 0 && <span className={styles.fileCardPreviewSize}>{formatFileSize(att.size)}</span>}
        </div>
        {actionEl}
      </div>

      {/* preview body */}
      {previewLines && previewLines.length > 0 && (
        <div className={styles.fileCardPreviewBody}>
          {previewLines.map((line, i) => {
            const trimmed = line.trim();
            if (trimmed.startsWith('#')) {
              return <div key={i} className={styles.previewH}>{trimmed}</div>;
            }
            if (trimmed.startsWith('```') || trimmed.startsWith('<') || trimmed.startsWith('//') || trimmed.startsWith('import ') || trimmed.startsWith('const ') || trimmed.startsWith('function ')) {
              return <div key={i} className={styles.previewCode}>{trimmed}</div>;
            }
            if (!trimmed) return <div key={i} style={{ height: 6 }} />;
            return <div key={i} className={styles.previewP}>{trimmed}</div>;
          })}
          <div className={styles.previewFade} />
        </div>
      )}
    </div>
  );

  return card;
});

const StarRating = memo(function StarRating() {
  const [rating, setRating] = useState(0);
  const [hoverStar, setHoverStar] = useState(0);
  const [locked, setLocked] = useState(false);
  const [showPerfect, setShowPerfect] = useState(false);

  const handleClick = (star: number) => {
    if (locked) return;
    setRating(star);
    setLocked(true);
    if (star === 5) {
      setShowPerfect(true);
      setTimeout(() => setShowPerfect(false), 1600);
    }
  };

  return (
    <div className={`${styles.ratingRow} ${locked ? styles.ratingLocked : ''}`} onMouseLeave={() => !locked && setHoverStar(0)}>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          className={`${styles.ratingStar} ${star <= rating ? styles.ratingStarActive : ''} ${!locked && hoverStar > 0 && star <= hoverStar && star > rating ? styles.ratingStarHover : ''} ${locked && star <= rating ? styles.ratingStarLocked : ''}`}
          onClick={() => handleClick(star)}
          onMouseEnter={() => !locked && setHoverStar(star)}
          aria-label={`Rate ${star}`}
          disabled={locked}
        >
          {star <= (locked ? rating : (hoverStar || rating)) ? '\u2605' : '\u2606'}
        </button>
      ))}
      {showPerfect && <span className={styles.ratingPerfect}>✨</span>}
    </div>
  );
});

function ForwardPicker({ onPick, onClose }: { onPick: (botName: string) => void; onClose: () => void }) {
  const bots = useStore((s) => s.bots);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);
  return (
    <div ref={ref} className={styles.fwdPicker}>
      <div className={styles.fwdPickerTitle}>{uiText("转发到")}</div>
      {bots.map((bot) => {
        const av = getAvatar(bot.name);
        return (
          <button key={bot.name} className={styles.fwdPickerItem} onClick={() => onPick(bot.name)}>
            <Avatar name={bot.name} size="sm" imageUrl={av.imageUrl} presetColor={av.presetColor} />
            <span className={styles.fwdPickerName}>{bot.name}</span>
          </button>
        );
      })}
      {bots.length === 0 && <div className={styles.fwdPickerEmpty}>{uiText("无可用 Agent")}</div>}
    </div>
  );
}

const MessageBubble = memo(function MessageBubble({ msg, botName, sessionTitle, dmPartner, onFileClick, onForward, t, isLast, onQuickReply }: { msg: ChatMessage; botName: string; sessionTitle?: string; dmPartner?: { name?: string; avatarUrl?: string } | null; onFileClick: (f: FileAttachment) => void; onForward: (text: string) => void; t: (key: string) => string; isLast?: boolean; onQuickReply?: (text: string) => void }) {
  // For DM sessions, use partner's real name instead of raw botName (dm:userId)
  const isDmSession = botName.startsWith('dm:');
  const displayName = isDmSession ? (dmPartner?.name || sessionTitle || 'User') : (msg.botName || botName);

  if (msg.type === 'system') {
    return (
      <div className={styles.systemMsg}>
        <span className={styles.systemText}>{msg.text}</span>
      </div>
    );
  }

  if (msg.type === 'user') {
    // In group chats, messages from other humans have botName set to their display name
    const isOtherHuman = !!msg.botName;
    const userName = isOtherHuman ? msg.botName : (localStorage.getItem('novax:displayName') || 'You');
    // Older local sessions used the attachment filenames as placeholder text for
    // file-only messages. Hide that generated placeholder now that attachments
    // render as proper cards, while preserving any text the user actually typed.
    const userText = getVisibleUserText(msg);
    return (
      <div className={styles.userRow}>
        {isOtherHuman
          ? <Avatar name={msg.botName!} size="sm" noTexture className={styles.userAvatar} />
          : (() => {
              const uAvatar = localStorage.getItem('novax:userAvatar');
              const uName = localStorage.getItem('novax:displayName') || 'You';
              return uAvatar
                ? <Avatar name={uName} size="sm" noTexture imageUrl={uAvatar} className={styles.userAvatar} />
                : <Avatar name={uName} size="sm" noTexture className={styles.userAvatar} />;
            })()
        }
        <div className={styles.userContent}>
          <div className={styles.userHeader}>
            <span className={styles.userName}>{userName}</span>
            <span className={styles.msgTime}>{formatTime(msg.timestamp)}</span>
          </div>
          <div className={styles.userBubble}>
            {userText && <div className={styles.userText}><MentionedText text={userText} /></div>}
            {msg.attachments && msg.attachments.length > 0 && (
              <div className={`${styles.fileCards} ${userText ? styles.userFileCardsWithText : ''}`}>
                {msg.attachments.map((att, i) => {
                  if (att.type.startsWith('image/')) {
                    return (
                      <button key={i} type="button" className={styles.fileCardImage} onClick={() => onFileClick(att)}>
                        <AuthenticatedImage src={att.url} alt={att.name} loading="lazy" />
                        <div className={styles.fileCardImageName}>{att.name}</div>
                      </button>
                    );
                  }
                  return <FilePreviewCard key={i} att={att} onFileClick={onFileClick} />;
                })}
              </div>
            )}
            {userText && (
              <button className={styles.fwdBtn} onClick={() => onForward(userText)} title={uiText("转发")}>
                <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 17 20 12 15 7" /><path d="M4 20v-5a4 4 0 014-4h12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // File-only message (no text, no state, only attachments)
  const isFileOnly = !msg.text && !msg.state && msg.attachments && msg.attachments.length > 0;
  if (isFileOnly) {
    return (
      <div className={styles.agentRow}>
        <BotAvatar name={displayName} size="sm" className={styles.agentAvatar} />
        <div className={styles.agentContent}>
          <div className={styles.agentHeader}>
            <span className={styles.agentName}>{displayName}</span>
            <span className={styles.msgTime}>{formatTime(msg.timestamp)}</span>
          </div>
          <div className={styles.fileCards}>
            {msg.attachments!.map((att, i) => {
              const isImage = att.type.startsWith('image/');
              if (isImage) {
                return (
                  <div key={i} className={styles.fileCardImage}>
                    <AuthenticatedImage src={att.url} alt={att.name} loading="lazy" />
                    <div className={styles.fileCardImageName}>{att.name}</div>
                  </div>
                );
              }
              return <FilePreviewCard key={i} att={att} onFileClick={onFileClick} />;
            })}
          </div>
        </div>
      </div>
    );
  }

  // Assistant message
  const isThinking = msg.state?.status === 'thinking';
  const isRunning = msg.state?.status === 'running';
  const isError = msg.state?.status === 'error';
  const isComplete = msg.state?.status === 'complete' || isError;
  const text = msg.text || msg.state?.responseText || '';
  const toolCalls = msg.state?.toolCalls || [];
  const hasActiveTools = toolCalls.some((tc) => tc.status === 'running');
  const visibleActivityKinds = [...new Set(toolCalls.map((tool) => activityKind(tool)))].slice(0, 5);
  const [logExpanded, setLogExpanded] = useState(false);
  // Activity log: expanded while running, collapsed when complete (user can toggle)
  const showLogItems = toolCalls.length > 0 && (!isComplete || logExpanded);

  return (
    <div className={styles.agentRow}>
      {isDmSession
        ? <Avatar name={displayName} size="sm" imageUrl={dmPartner?.avatarUrl} className={styles.agentAvatar} />
        : <BotAvatar name={displayName} size="sm" className={styles.agentAvatar} />
      }
      <div className={styles.agentContent}>
        <div className={styles.agentHeader}>
          <span className={styles.agentName}>{displayName}</span>
          <span className={styles.msgTime}>{formatTime(msg.timestamp)}</span>
        </div>
        <div className={styles.agentBubble}>

        {/* Thinking indicator — only when no tools and no text yet */}
        {(isThinking || (isRunning && !text && toolCalls.length === 0)) && (
          <div className={styles.thinking}>
            <div className={styles.thinkingDots}><span /><span /><span /></div>
            <span className={styles.thinkingLabel}>{t('msg.thinking')}</span>
          </div>
        )}

        {/* Activity log — collapsible execution trace */}
        {toolCalls.length > 0 && (
          <div className={styles.activityLog}>
            {/* Collapsed summary — clickable to toggle */}
            {isComplete && (
              <button className={styles.activityToggle} onClick={() => setLogExpanded((v) => !v)}>
                <span className={`${styles.activityToggleArrow} ${logExpanded ? styles.activityToggleOpen : ''}`}>&#9656;</span>
                <span className={styles.activityToggleLabel}>
                  {uiText("已完成")} {toolCalls.length} {uiText("项操作")}
                </span>
                <span className={styles.activityKindSummary} aria-hidden="true">
                  {visibleActivityKinds.map((kind) => (
                    <span key={kind} className={`${styles.activitySummaryIcon} ${activityKindClass(kind)}`}>
                      {activityIcon(kind)}
                    </span>
                  ))}
                </span>
              </button>
            )}
            {/* Expanded items */}
            {showLogItems && (
              <div className={`${styles.activityItems} ${isComplete ? styles.activityItemsDone : ''}`}>
                {toolCalls.map((tc, i) => (
                  <div key={tc.id || i} className={`${styles.activityItem} ${tc.status === 'running' ? styles.activityRunning : tc.status === 'error' ? styles.activityFailed : styles.activityDone}`}>
                    <span className={`${styles.activityKindIcon} ${activityKindClass(activityKind(tc))}`} aria-hidden="true">
                      {activityIcon(activityKind(tc))}
                    </span>
                    <span className={styles.activityLabel}>{formatToolLabel(tc.name)}</span>
                    {tc.detail && <span className={styles.activityDetail}>{tc.detail}</span>}
                    <span className={styles.activityState} aria-label={tc.status === 'running' ? uiText("执行中") : tc.status === 'error' ? uiText("失败") : uiText("完成")}>
                      {tc.status === 'running' ? <span className={styles.activityDot} /> : tc.status === 'error' ? '!' : '✓'}
                    </span>
                  </div>
                ))}
                {/* Thinking after tools — model is processing tool results */}
                {isRunning && !text && !hasActiveTools && (
                  <div className={`${styles.activityItem} ${styles.activityRunning}`}>
                    <span className={`${styles.activityKindIcon} ${styles.activityKindReasoning}`}>✦</span>
                    <span className={styles.activityLabel}>{t('msg.thinking')}</span>
                    <span className={styles.activityState}><span className={styles.activityDot} /></span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Response text — below the activity log */}
        {text && (
          <div className={styles.markdown}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw, [rehypeSanitize, assistantMarkdownSchema], rehypeHighlight]}
              components={{ a: ({ children, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer">{children}</a> }}
            >
              {preprocessMarkdown(text)}
            </ReactMarkdown>
          </div>
        )}

        {isError && (
          <div className={styles.errorMsg}>
            {msg.state?.errorMessage || t('msg.error')}
          </div>
        )}

        {isComplete && (msg.state?.creditsUsed != null || msg.state?.costUsd != null) && (
          <div className={styles.costInfo}>
            {msg.state.model && <span className={styles.costModel}>{msg.state.model}</span>}
            {msg.state.creditsUsed != null
              ? `${msg.state.creditsUsed.toLocaleString('zh-CN')} Nova 点`
              : `$${msg.state.costUsd!.toFixed(4)}`}
            {msg.state.creditsRemaining != null
              && ` · 组织剩余 ${msg.state.creditsRemaining.toLocaleString('zh-CN')} 点`}
            {msg.state.durationMs != null && ` · ${(msg.state.durationMs / 1000).toFixed(1)}s`}
          </div>
        )}

        {msg.attachments && msg.attachments.length > 0 && (
          <>
            {/* Images: render inline directly */}
            {msg.attachments.filter((att) => att.type.startsWith('image/')).map((att, i) => (
              <div key={`img-${i}`} className={styles.inlineImage}>
                <AuthenticatedImage src={att.url} alt={att.name} loading="lazy" />
              </div>
            ))}
            {/* Non-image files: clickable cards */}
            {msg.attachments.filter((att) => !att.type.startsWith('image/')).length > 0 && (
              <div className={styles.attachments}>
                {msg.attachments.filter((att) => !att.type.startsWith('image/')).map((att, i) =>
                  canPreviewInline(att) ? (
                    <button key={i} className={styles.attachmentCard} onClick={() => onFileClick(att)}>
                      <span className={styles.attachIcon}>{getAttachIcon(att)}</span>
                      <span className={styles.attachName}>{att.name}</span>
                    </button>
                  ) : (
                    <button key={i} type="button" className={styles.attachmentCard} onClick={() => { void downloadAuthenticatedFile(att); }}>
                      <span className={styles.attachIcon}>{getAttachIcon(att)}</span>
                      <span className={styles.attachName}>{att.name}</span>
                    </button>
                  )
                )}
              </div>
            )}
          </>
        )}
          {isComplete && text && (
            <button className={styles.fwdBtn} onClick={() => onForward(text)} title={uiText("转发")}>
              <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 17 20 12 15 7" /><path d="M4 20v-5a4 4 0 014-4h12" />
              </svg>
            </button>
          )}
        </div>

        {/* Rating — outside the bubble */}
        {isComplete && text && !isDmSession && (
          <StarRating />
        )}

        {/* Quick reply suggestions — only on the last completed message */}
        {isLast && isComplete && !isError && onQuickReply && msg.state?.quickReplies && msg.state.quickReplies.length > 0 && (
          <div className={styles.quickReplies}>
            {msg.state.quickReplies.map((reply, i) => (
              <button key={i} className={styles.quickReplyBtn} onClick={() => onQuickReply(reply)}>
                {reply}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

/* ═══ Message Navigation Panel ═══ */
function MsgNavPanel({ messages, onJump, onClose }: {
  messages: ChatMessage[];
  onJump: (id: string) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return messages;
    const q = search.trim().toLowerCase();
    return messages.filter((m) => {
      const text = m.text || m.state?.responseText || '';
      return text.toLowerCase().includes(q);
    });
  }, [messages, search]);

  const getPreview = (msg: ChatMessage): string => {
    const text = msg.text || msg.state?.responseText || '';
    const fileNames = msg.attachments?.length
      ? msg.attachments.map((f) => f.name).join(', ')
      : '';
    if (!text) {
      if (fileNames) return `📎 ${fileNames}`;
      if (msg.state?.status === 'thinking') return uiText("思考中...");
      return '...';
    }
    const preview = text.replace(/\n/g, ' ').slice(0, 60);
    return fileNames ? `${preview} 📎 ${fileNames}` : preview;
  };

  const senderName = localStorage.getItem('novax:displayName') || '你';

  return (
    <div className={styles.navPanel}>
      <div className={styles.navHeader}>
        <span className={styles.navCount}>{filtered.length}/{messages.length}</span>
        <button className={styles.navClose} onClick={onClose} aria-label={uiText("关闭消息导航")}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
      <div className={styles.navSearch}>
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="11" cy="11" r="7" /><path d="M16.5 16.5L21 21" />
        </svg>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={uiText("搜索消息...")}
          className={styles.navSearchInput}
          autoFocus
        />
      </div>
      <div className={styles.navList}>
        {filtered.length === 0 ? (
          <div className={styles.navEmpty}>{uiText("没有匹配的消息")}</div>
        ) : (
          filtered.map((msg) => (
            <button key={msg.id} className={styles.navItem} onClick={() => onJump(msg.id)}>
              <span className={`${styles.navDot} ${styles.navDotUser}`} />
              <div className={styles.navItemContent}>
                <span className={styles.navItemSender}>{senderName}</span>
                <span className={styles.navItemPreview}>{getPreview(msg)}</span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
